import { Download, Info, Link, Unlink } from 'lucide-react';
import {
  Button,
  Divider,
  Group,
  Image,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useAuthUser } from '@store/useAuthStore';
import { Trans, useTranslation } from 'react-i18next';
import { config } from '../../config/env';
import { DeviceManagement } from '../../components/blocks/Device';
import PageHeader from '../../components/shared/PageHeader';

const MONITOR_APK_VERSION_RE = /become-monitor-(v\d+\.\d+\.\d+)\.apk/i;

export function getMonitorApkVersion(apkUrl: string): string | null {
  const match = apkUrl.match(MONITOR_APK_VERSION_RE);
  return match?.[1] ?? null;
}

export function Devices() {
  const { t } = useTranslation();
  const currentUser = useAuthUser();
  const monitorApkUrl = config.MONITOR_APK_URL?.trim();
  const monitorApkVersion = monitorApkUrl
    ? getMonitorApkVersion(monitorApkUrl)
    : null;
  const monitorApkQrCodeUrl = monitorApkUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(monitorApkUrl)}`
    : '';

  const [infoOpened, { open: openInfo, close: closeInfo }] =
    useDisclosure(false);
  const [monitorApkOpened, { open: openMonitorApk, close: closeMonitorApk }] =
    useDisclosure(false);

  return (
    <>
      <PageHeader
        title={t('all_devices', 'ACCESSO AI DISPOSITIVI')}
        subtitle='Trova qui i codici e le password per accedere ai dispositivi'
        actions={
          <Group>
            <Button
              variant='light'
              onClick={() =>
                window.open('mailto:support@become-hub.com', '_blank')
              }
            >
              Contatta supporto
            </Button>
            <Button variant='light' onClick={openMonitorApk}>
              <Group gap={8}>
                <Download size={16} />
                <Text component='span' size='sm'>
                  Scarica Become Monitor App
                  {monitorApkVersion ? ` (${monitorApkVersion})` : ''}
                </Text>
              </Group>
            </Button>
            <Button variant='outline' onClick={openInfo}>
              <Info size={24} />
            </Button>
          </Group>
        }
      />

      <Divider mb='md' mt='md' />

      <Modal
        opened={monitorApkOpened}
        onClose={closeMonitorApk}
        title='Scarica Become Monitor App'
        size='lg'
      >
        <Stack gap='md' align='center'>
          <Text ta='center'>
            Become Monitor App permette di tracciare in tempo reale parametri
            fisiologici come HR, HRV e altri indicatori cardiaci, collegando il
            sensore Polar al tuo smartphone.
          </Text>
          {monitorApkVersion ? (
            <Text ta='center' fw={600}>
              Versione attuale: {monitorApkVersion}
            </Text>
          ) : null}
          <Text ta='center' size='sm' c='dimmed'>
            Inquadra il QR code con il telefono per scaricare direttamente
            l&apos;APK e completare l&apos;installazione.
          </Text>
          {monitorApkUrl ? (
            <>
              <Image
                src={monitorApkQrCodeUrl}
                alt='QR code per scaricare Become Monitor App'
                w={280}
                h={280}
              />
              <Button
                component='a'
                href={monitorApkUrl}
                target='_blank'
                rel='noreferrer'
                leftSection={<Download size={16} />}
              >
                Scarica APK
                {monitorApkVersion ? ` (${monitorApkVersion})` : ''}
              </Button>
            </>
          ) : (
            <Text c='red' size='sm' ta='center'>
              Link APK non configurato. Imposta `VITE_MONITOR_APK_URL` per
              abilitare il download.
            </Text>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={infoOpened}
        onClose={closeInfo}
        title='Informazioni'
        size='lg'
      >
        <Trans i18nKey='all_devices_info'>
          <Text>
            In questa pagina puoi gestire le <b>connessioni</b> tra i
            dispositivi. Puoi aggiungere visori e sensori senza limiti: ogni
            visore può avere al massimo 1 sensore collegato.
          </Text>
          <br />
          <Text>
            Per collegare un sensore al VR clicca sull'icona{' '}
            <Link size={16} />. Per scollegarlo clicca sull'icona{' '}
            <Unlink size={16} />.
          </Text>
        </Trans>
      </Modal>

      <DeviceManagement
        userId={currentUser?.id?.toString()}
        hideConnectionBadges={false}
        hideCreateButtons={false}
        showDeviceList={true}
        showTokenTable={true}
      />
    </>
  );
}
